import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '../../buttons/Buttons';
import PartnersLogoSection from './PartnerOrganisations';

const DirectHero = ({
  mainTitle = {
    prefix: "AI for", 
    highlight: "plastic spectral analysis"
  },
  description = "PlasticAnalytics' Spectral Library uses AI-powered spectroscopic analysis to aid researchers rapidly identify, quantify, and monitor plastic compounds with unmatched precision.",
  ctaText = "Get Started",
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    // Trigger animations after component mounts
    setIsLoaded(true);
  }, []);

  const partnerLogos = [
    {
      id: 1,
      name: "Partner 1",
      logo: "https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Fimperial-college-london-logo.png?alt=media&token=a2e5bf66-50d7-4be9-ade4-8c53538439d4",
    },
    {
      id: 2,
      name: "Partner 2",
      logo: "https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Fmrc-centre-logo.png?alt=media&token=5b047c0f-3a34-4add-9547-fecad60e109f",
    },
    {
      id: 3,
      name: "Partner 3",
      logo: "https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Flogos%2Fmedical-research-council-logo.png?alt=media&token=ca91e8d4-052f-453f-9752-56369a416265",
    },
  ];

  const handleGetStarted = () => {
    window.location.href = '/spectral-library/analyse';
  };

  return (
    <div className={`hero-container ${isLoaded ? 'loaded' : ''}`}>
      <div className="hero-content">
        <h1 className="hero-title">
          <span className="title-prefix">{mainTitle.prefix}</span>{' '}
          <span className="title-highlight">{mainTitle.highlight}</span>
        </h1>
        <p className="hero-description">{description}</p>
        <div className="cta-container">
          <Button type="primary" onClick={handleGetStarted}>
            {ctaText}
          </Button>
        </div>
      </div>
      
      <div className="partners-section">
        <PartnersLogoSection 
          title="Partners" 
          logos={partnerLogos}
          backgroundColor="#f8f9fa"
        />
      </div>
      
      <style jsx>{`
        .hero-container {
          width: 100%;
          background: white;
          color: #000000;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 3rem 2rem;
          box-sizing: border-box;
          opacity: 0;
          transition: opacity 0.5s ease-out;
        }
        
        .hero-container.loaded {
          opacity: 1;
        }
        
        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          text-align: left;
          padding: 3rem 2rem;
        }
        
        .hero-title {
          font-size: 4.5rem;
          line-height: 1.1;
          letter-spacing: -0.02em;
          max-width: 80rem;
          padding-top: 2rem;
          margin-bottom: 1.5rem;
          font-weight: normal;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s forwards;
          animation-delay: 0.2s;
        }
        
        .title-prefix {
          font-weight: 700;
          display: inline-block;
          font-family: 'Helvetica', sans-serif;
        }
        
        .title-highlight {
          font-weight: 400;
          display: inline-block;
          font-style: italic;
          font-family: "Space Grotesk", sans-serif;
          position: relative;
        }
        
        .title-highlight:after {
          content: '';
          position: absolute;
          width: 0;
          height: 4px;
          bottom: -15px;
          left: 0;
          background-color: #1a2a6c;
          animation: lineAnimation 1.2s forwards;
          animation-delay: 1s;
        }
        
        .hero-description {
          font-size: 1.5rem;
          max-width: 800px;
          margin: 0 0 2.5rem;
          line-height: 1.4;
          color: #555;
          font-weight: 400;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s forwards;
          animation-delay: 0.4s;
        }
        
        .cta-container {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s forwards;
          animation-delay: 0.6s;
        }
        
        .partners-section {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeInUp 0.8s forwards;
          animation-delay: 0.8s;
          width: 100%;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes lineAnimation {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
        
        @media (max-width: 1024px) {
          .hero-title {
            font-size: 3.5rem;
          }
          
          .hero-description {
            font-size: 1.3rem;
          }
        }
        
        @media (max-width: 768px) {
          .hero-content {
            padding: 2rem 1rem;
          }
          
          .hero-title {
            font-size: 3rem;
          }
          
          .hero-description {
            font-size: 1.2rem;
            margin: 0 auto 2.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .hero-container {
            padding: 1rem;
          }
          
          .hero-content {
            padding: 1.5rem 0.5rem;
          }
          
          .hero-title {
            font-size: 2.5rem;
          }
          
          .hero-description {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

DirectHero.propTypes = {
  mainTitle: PropTypes.shape({
    prefix: PropTypes.string,
    highlight: PropTypes.string
  }),
  description: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string
};

export default DirectHero;